import { useEffect, useRef, useState } from 'react';

import { dampAngle } from '../../engine/math/mathx.js';
import { STALE_MS, useBlipFeed } from '../hooks/useBlipFeed.js';
import { MARKER_COLORS, screenBearing } from './mapRender.js';

/**
 * @file THE DRIVING GUIDE ARROW: one chevron and one distance, bottom centre,
 * pointing at where the tracked mission wants the player to go.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT EXISTS — THE MINIMAP WAS ALREADY CORRECT AND STILL DID NOT WORK
 * ─────────────────────────────────────────────────────────────────────────────
 * Mission markers have been on both maps since the director shipped, edge-
 * clamped so they can never fall off the minimap, drawn OVER the blips and with
 * a label. None of that was the problem. **Players driving did not look at the
 * minimap at all** — at 17 m/s the eyes are on the road, the minimap is a 220 px
 * window in the far corner of the screen, and a 5 px ring clamped to its rim is
 * a thing you find when you go looking for it, not a thing you notice.
 *
 * So this is not a second map and it must never grow into one. It answers ONE
 * question — *which way do I turn* — in the place the player is already looking,
 * and it deliberately carries no north, no rotation the player has to mentally
 * undo, no route and no other markers. Everything else stays the minimap's job.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ IT COMPUTES NO ORIENTATION OF ITS OWN
 * ─────────────────────────────────────────────────────────────────────────────
 * The bearing comes from `mapRender.js#screenBearing`, which is the minimap's
 * own transform with the map thrown away. Writing the `atan2` here would be a
 * SECOND definition of "which way is up", and the failure mode of a second copy
 * is a MIRROR — an arrow that looks completely right in a screenshot and sends
 * the player the wrong way in motion. `tests/minimap.test.mjs` section (a)
 * exists because this project has already shipped that bug once on the map
 * itself; section (k) pins this function against the same convention.
 *
 * ⚠ THE VIEWER POSITION IS `engine.target`, NOT THE BLIP FEED. `'hud:blips'` is
 * an 8 Hz channel and this thing is read while cornering at the limiter, so a
 * feed-driven bearing would step visibly. `engine.target` is the CameraRig's
 * per-frame focus — the car, while driving — which makes the arrow agree with
 * the screen by construction rather than by luck: it is measured from the point
 * the camera is looking at, through the yaw the camera is using. The feed is
 * still what supplies the MARKERS (they are the director's, not the camera's)
 * and the `driving` flag, and `stamp` is still what says the whole thing is live.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT TURNS IT OFF, IN THE ORDER IT IS ASKED
 * ─────────────────────────────────────────────────────────────────────────────
 *  1. the feed is stale (dev scene, no gameplay, frozen loop) — nothing to point
 *     at and nothing to point FROM;
 *  2. the player is not driving. ⚠ ONE BOOLEAN, on purpose: the reported problem
 *     is a driving one, and an arrow that is up for the whole game is chrome
 *     rather than guidance. Widening it later is this one test;
 *  3. no marker survives {@link pickTarget};
 *  4. the player is INSIDE the marker's own radius. ⚠ That bound is `mk.r` —
 *     the director's number for "you have arrived" — and NOT a constant of ours:
 *     a `goto` circle, a chase catch radius and a giver ring are all different
 *     sizes and all mean the same thing here. Inside it the bearing is degenerate
 *     noise anyway. The re-show threshold is 1.5x that, so standing on the rim
 *     cannot strobe the arrow.
 *
 * ⚠ NO PERMISSION, NO SECOND BOOK OF MISSION STATE. It reads the marker list the
 * map already gets and picks one; it never asks whether a mission is active,
 * never subscribes to `'mission:list'`, and has no idea what a mission IS. The
 * director decides what is tracked (`Missions.js#trackedId`) and this points at
 * the result — two places ranking missions is the `hurtPlayer(amount, lethal)`
 * family of bug.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT RE-RENDERS ONLY WHEN THE TARGET CHANGES KIND
 * ─────────────────────────────────────────────────────────────────────────────
 * The `Minimap.jsx` rule. The angle and the distance are written straight onto
 * the DOM nodes from the rAF loop, because they change 30 times a second and
 * React owning them would re-render the HUD tree for a `transform`. Only the two
 * things that change at human pace — whether it is up at all, and what colour it
 * is — go through `useState`, and both are edge-guarded.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 */

/** Update ceiling, ms. rAF drives the loop; this is the throttle. 30 Hz is
 *  smooth for a rotation and a third of the work of an uncapped one. */
const TICK_MS = 33;

/**
 * Bearing follow stiffness, 1/s (`mathx.damp` lambda). Deliberately STIFF —
 * this is a navigation instrument, and an arrow that lags a turn is worse than
 * one that jitters. It is here at all because the MARKER moves at 8 Hz (and a
 * `chase` marker rides a live actor, so it really does move), not because the
 * camera does: `engine.yaw` already arrives smoothed by `CameraRig`. `dampAngle`
 * takes the short way round, so an arrow crossing behind the player sweeps the
 * near side instead of unwinding a full turn.
 */
const LAMBDA = 22;

/** Below this the readout switches to kilometres, so it never runs to 4 digits. */
const KM_AT = 1000;

/**
 * WHICH marker the arrow points at, in one pass over the map's own list. The
 * order IS the policy and it is written out rather than scored:
 *
 *  1. the TRACKED marker that is not dimmed — the director's own answer to
 *     "what is the player doing", and the same one `MissionPanel` prints;
 *  2. any live objective — a mission is running and something went wrong with
 *     tracking; pointing at the work beats pointing at nothing;
 *  3. any undimmed giver — no mission running, so the useful direction is the
 *     next job. A DIMMED giver (available, but another mission is already
 *     active) is never chosen: the player cannot take it, and an arrow toward a
 *     door that will refuse them is worse than no arrow.
 *
 * @param {import('../hooks/useBlipFeed.js').BlipFeed} feed
 * @returns {any} A marker record from the feed (do not retain — recycled), or null.
 */
function pickTarget(feed) {
  let objective = null;
  let giver = null;
  for (let i = 0; i < feed.markerCount; i++) {
    const mk = feed.markers[i];
    if (mk.dim) continue;
    if (mk.tracked) return mk;
    if (!objective && mk.kind === 'objective') objective = mk;
    else if (!giver) giver = mk;
  }
  return objective ?? giver;
}

/**
 * Metres → the string on the pill. Whole metres under a kilometre (a decimal
 * there is precision nobody can use at 17 m/s), one decimal above it.
 * @param {number} m
 * @returns {string}
 */
function distanceText(m) {
  if (m >= KM_AT) return `${(m / 1000).toFixed(1)} km`;
  return `${Math.round(m)} m`;
}

export default function MissionArrow({ engine }) {
  const feed = useBlipFeed(engine);
  /** The only React state: up/down, and which of the map's marker colours. */
  const [shown, setShown] = useState(/** @type {string|null} */ (null));
  const dialRef = useRef(null);
  const distRef = useRef(null);

  useEffect(() => {
    let raf = 0;
    let last = 0;
    /** Live bearing, radians. NaN = "no history", so the first frame SNAPS
     *  rather than sweeping in from zero. */
    let angle = NaN;
    let up = false;
    let colour = null;
    let text = '';

    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      if (now - last < TICK_MS) return;
      // CLAMPED: the first tick's `last` is 0, and a backgrounded tab hands back
      // a gap of minutes. Either would make the damping a snap — harmless here,
      // but only by accident, and `dampAngle` is not the place to find that out.
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;

      const f = feed.current;
      const fresh = Date.now() - f.player.stamp < STALE_MS;
      const mk = fresh && f.player.driving ? pickTarget(f) : null;

      // The camera's own focus — see the header. `engine.target` is a
      // THREE.Vector3 the rig writes every frame; we only ever read two floats
      // off it and never retain it.
      const t = engine?.target;
      const dx = mk && t ? mk.x - t.x : 0;
      const dz = mk && t ? mk.z - t.z : 0;
      const dist = Math.hypot(dx, dz);

      // ⚠ The arrival bound is the MARKER's radius, and the re-show is 1.5x it:
      // one threshold would strobe for anyone parked on the rim.
      const arrived = mk ? dist <= (up ? mk.r : mk.r * 1.5) : true;
      const want = !!mk && !arrived;

      if (!want) {
        if (up) {
          up = false;
          angle = NaN;
          colour = null;
          // ⚠ AND THE CACHED LABEL, or the re-show is a pill reading `—` until
          // the distance happens to change: hiding UNMOUNTS the node, so the
          // next one starts blank while `text` still remembers the old string
          // and suppresses the write that would fill it.
          text = '';
          setShown(null);
        }
        return;
      }

      const next = MARKER_COLORS[mk.kind] ?? MARKER_COLORS.objective;
      if (!up || next !== colour) {
        up = true;
        colour = next;
        setShown(next);
      }

      const target = screenBearing(engine?.yaw ?? 0, dx, dz);
      angle = Number.isFinite(angle) ? dampAngle(angle, target, LAMBDA, dt) : target;
      const dial = dialRef.current;
      if (dial) dial.style.transform = `rotate(${(angle * 180) / Math.PI}deg)`;

      // ⚠ `text` CACHES WHAT WAS PAINTED, NEVER WHAT WAS COMPUTED, and the two
      // come apart on exactly one frame — the one that turns the arrow on.
      // `setShown` above does not commit synchronously, so `distRef.current` is
      // still null on that tick; caching the label there marks it written, the
      // next tick sees no change and skips the write, and a pill on a STATIONARY
      // car reads `—` for ever because the distance never moves to unstick it.
      // FOUND IN THE BROWSER (parked at a marker), invisible to every suite.
      const label = distanceText(dist);
      const node = distRef.current;
      if (node && label !== text) {
        text = label;
        node.textContent = label;
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [engine, feed]);

  if (!shown) return null;

  return (
    // NO z-index, exactly like `Prompt` and `PhoneRing`: this belongs UNDER
    // `FadeOverlay` (z-30), so driving through a door takes it with the rest of
    // the screen instead of leaving an arrow floating over a black frame. It
    // docks ABOVE the ring banner (bottom-28, ~2.1rem tall) the same way that
    // banner docks above the prompt — a call can arrive mid-drive, and the three
    // lines stack rather than overlap.
    <div className="pointer-events-none absolute inset-x-0 bottom-40 flex select-none justify-center">
      {/* ⚠ `role="group"` AND DELIBERATELY NOT `role="status"` — the `AmmoBar`
          rule. The distance is rewritten up to 30 times a second, and a live
          region would read every one of them aloud. This is a labelled group a
          screen reader can go and find, never one that announces itself. */}
      <div
        className="flex items-center gap-2 rounded-full border bg-void/75 py-1.5 pl-2.5 pr-3.5 backdrop-blur-sm"
        style={{ borderColor: `${shown}55` }}
        role="group"
        aria-label="Distance to the current objective"
      >
        {/* The chevron is authored pointing UP, which is what lets the bearing
            be applied raw: `screenBearing` returns 0 for "straight ahead" and
            grows clockwise, and so does CSS `rotate`. The drop shadow is what
            keeps a white objective arrow legible over pale sunlit tarmac — the
            same reason the speech bubbles carry a dark glow behind the
            portrait mask. */}
        <span
          ref={dialRef}
          aria-hidden="true"
          className="block h-[18px] w-[18px] will-change-transform"
          style={{ filter: 'drop-shadow(0 0 3px rgba(5,7,11,0.95))' }}
        >
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path d="M12 2.5 21 21 12 16.4 3 21z" fill={shown} stroke="#05070b" strokeWidth="1.4" strokeLinejoin="round" />
          </svg>
        </span>
        <span
          ref={distRef}
          className="font-hud text-[13px] uppercase leading-none tracking-[0.16em] text-hud tabular-nums"
        >
          {/* Painted imperatively from the loop; this is only the first frame. */}
          —
        </span>
      </div>
    </div>
  );
}
